// Left-rail list of Object Types with skeleton loading, empty state, and selection.

import { useState } from "react";
import { Layers, Plus, Table2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ObjectType } from "@/types/ontology";
import * as LucideIcons from "lucide-react";
import { ObjectDataViewer } from "@/components/ontology/ObjectDataViewer";

function toPascalCase(kebab: string): string {
  return kebab
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join("");
}

function ObjectTypeIcon({ icon, className }: { icon: string | null; className?: string }) {
  if (!icon) return <Layers className={className ?? "w-3.5 h-3.5"} />;
  const pascalName = toPascalCase(icon);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Icon = (LucideIcons as Record<string, any>)[pascalName];
  if (!Icon) return <Layers className={className ?? "w-3.5 h-3.5"} />;
  return <Icon className={className ?? "w-3.5 h-3.5"} />;
}

interface ObjectTypeListProps {
  objectTypes: ObjectType[];
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

export function ObjectTypeList({
  objectTypes,
  isLoading,
  selectedId,
  onSelect,
}: ObjectTypeListProps) {
  // Object Type id whose materialized records are being browsed (null = closed).
  const [viewingDataId, setViewingDataId] = useState<string | null>(null);

  return (
    <div className="h-full flex flex-col p-4" aria-busy={isLoading}>
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 px-1">
        Object Types
      </p>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-1">
        {isLoading ? (
          // Skeleton loading state — 5 rows
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 rounded-md" />)
        ) : objectTypes.length === 0 ? (
          // Empty state
          <div className="flex flex-col items-center justify-center h-40 gap-3 text-center">
            <Layers className="w-10 h-10 text-muted-foreground opacity-40" />
            <div>
              <p className="text-sm font-medium">No object types yet</p>
              <p className="text-xs text-muted-foreground">Create one to start.</p>
            </div>
            <Button size="sm" onClick={() => onSelect(null)}>
              New Object Type
            </Button>
          </div>
        ) : (
          objectTypes.map((ot) => (
            <div
              key={ot.id}
              className={cn(
                "group flex items-center rounded-md transition-colors",
                selectedId === ot.id ? "bg-indigo-500/15" : "hover:bg-white/[0.06]"
              )}
            >
              <button
                type="button"
                aria-pressed={selectedId === ot.id}
                onClick={() => onSelect(ot.id)}
                className={cn(
                  "flex-1 min-w-0 flex items-center gap-2 px-2.5 py-2 rounded-md text-left text-sm transition-colors",
                  selectedId === ot.id
                    ? "text-indigo-300"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <ObjectTypeIcon
                  icon={ot.icon}
                  className={cn(
                    "w-3.5 h-3.5 flex-shrink-0",
                    selectedId === ot.id ? "text-indigo-300" : "text-muted-foreground"
                  )}
                />
                <span className="truncate font-medium">{ot.name}</span>
              </button>
              <button
                type="button"
                aria-label="View records"
                title={`View data for ${ot.name}`}
                onClick={() => setViewingDataId(ot.id)}
                className="flex-shrink-0 mr-1.5 p-1.5 rounded-md text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-foreground hover:bg-white/[0.08] transition-opacity"
              >
                <Table2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Footer: New Object Type button (shown when list is non-empty) */}
      {!isLoading && objectTypes.length > 0 && (
        <div className="pt-3 border-t border-border mt-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-1.5"
            onClick={() => onSelect(null)}
          >
            <Plus className="w-3.5 h-3.5" />
            New Object Type
          </Button>
        </div>
      )}

      <ObjectDataViewer
        objectTypeId={viewingDataId}
        open={viewingDataId !== null}
        onOpenChange={(next) => {
          if (!next) setViewingDataId(null);
        }}
      />
    </div>
  );
}
